import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Edit3, Film, ImageIcon, Trash2, Upload } from 'lucide-react'
import {
  formatBytes,
  resolveMediaUrl
} from '../../../api/client'
import {
  getVisualDerivativeUrl,
  type VisualAsset,
  type VisualAssetFilters,
  type VisualGroup,
  type VisualUploadResult
} from '../../../api/visuals'
import { Button, ChipSelect, EmptyState, IconButton, Modal, ModalActions, Pill, SearchField, StatusBadge } from '../../../components/ui'
import uploadStyles from '../../../components/ui/uploadDialogStyles.module.css'
import { formatDuration } from '../formatDuration'
import { formatVisualAssetMetadata } from '../formatVisualAssetMetadata'
import styles from './VisualLibrary.module.css'

type VisualLibraryProps = {
  filters: VisualAssetFilters
  groups: VisualGroup[]
  items: VisualAsset[]
  onDelete: (asset: VisualAsset) => Promise<void>
  onEdit: (asset: VisualAsset) => void
  onFilter: (filters: VisualAssetFilters) => Promise<void>
  onRegisterMobileUploadAction?: (action: (() => void) | null) => void
  onUploadComplete: () => Promise<void>
  onUploadFile: (
    file: File,
    groupIds: string[],
    onProgress: (progress: number) => void
  ) => Promise<VisualUploadResult>
  onUploadStateChange: (uploading: boolean) => void
  uploading: boolean
}

type UploadItemStatus = 'queued' | 'uploading' | 'complete' | 'skipped' | 'failed'

type UploadItem = {
  id: string
  file: File
  name: string
  progress: number
  size: number
  status: UploadItemStatus
  statusText: string | undefined
  type: 'image' | 'video'
}

const uploadClass = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => uploadStyles[className as string] ?? className)
    .join(' ')

export const VisualLibrary = (props: VisualLibraryProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [uploadGroupIds, setUploadGroupIds] = useState<string[]>([])
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([])
  const [draggingUpload, setDraggingUpload] = useState(false)
  const registerMobileUploadAction = props.onRegisterMobileUploadAction
  const toggleUploadGroup = (groupId: string): void => {
    setUploadGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    )
  }

  const updateFilter = (patch: VisualAssetFilters): void => {
    void props.onFilter({
      ...props.filters,
      ...patch
    })
  }

  const openUploadDialog = useCallback((): void => {
    setUploadItems([])
    setShowUploadDialog(true)
  }, [])

  useEffect(() => {
    registerMobileUploadAction?.(openUploadDialog)

    return () => {
      registerMobileUploadAction?.(null)
    }
  }, [openUploadDialog, registerMobileUploadAction])

  const closeUploadDialog = (): void => {
    setShowUploadDialog(false)
    setUploadGroupIds([])
    setUploadItems([])
    void props.onUploadComplete()

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const patchUploadItem = (id: string, patch: Partial<UploadItem>): void => {
    setUploadItems((current) =>
      current.map((item) => item.id === id ? { ...item, ...patch } : item)
    )
  }

  const uploadFiles = async (files: FileList | null): Promise<void> => {
    if (!files?.length) {
      return
    }

    const nextItems: UploadItem[] = Array.from(files).map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${file.size}-${index}`,
      file,
      name: file.name,
      progress: 0,
      size: file.size,
      status: 'queued',
      statusText: undefined,
      type: file.type.startsWith('image/') ? 'image' as const : 'video' as const,
    }))

    setUploadItems(nextItems)
    props.onUploadStateChange(true)

    try {
      for (const item of nextItems) {
        patchUploadItem(item.id, {
          progress: 0,
          status: 'uploading',
          statusText: undefined
        })

        try {
          const result = await props.onUploadFile(item.file, uploadGroupIds, (progress) => {
            patchUploadItem(item.id, { progress })
          })
          const skipped = result.skipped.find((skippedFile) => skippedFile.filename === item.name)

          if (skipped) {
            patchUploadItem(item.id, {
              progress: 100,
              status: 'skipped',
              statusText: 'Duplicate skipped'
            })
          } else {
            patchUploadItem(item.id, {
              progress: 100,
              status: 'complete',
              statusText: 'Queued for inspection'
            })
          }
        } catch (error) {
          patchUploadItem(item.id, {
            status: 'failed',
            statusText: error instanceof Error ? error.message : 'Upload failed'
          })
        }
      }
    } finally {
      props.onUploadStateChange(false)
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <>
      <div className="toolbar visual-toolbar">
        <Button className={['toolbar-upload', styles.visualUploadButton].join(' ')} icon={<Upload size={16} />} mobileFullWidth onClick={openUploadDialog} variant="primary">
          {props.uploading ? 'Uploading...' : 'Upload visual'}
        </Button>
        <SearchField
          className="toolbar-search-field"
          onChange={(event) => updateFilter({ search: event.target.value })}
          placeholder="Search visual assets..."
          value={props.filters.search ?? ''}
        />
        <select
          className="form-input select-input"
          onChange={(event) => updateFilter({ group: event.target.value })}
          value={props.filters.group ?? ''}
        >
          <option value="">All groups</option>
          {props.groups.map((group) => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </select>
        <select
          className="form-input select-input"
          onChange={(event) => updateFilter({
            type: event.target.value ? event.target.value as 'image' | 'video' : undefined
          })}
          value={props.filters.type ?? ''}
        >
          <option value="">Images and videos</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
        </select>
        <select
          className="form-input select-input"
          onChange={(event) => updateFilter({ status: event.target.value })}
          value={props.filters.status ?? ''}
        >
          <option value="">Any status</option>
          {['queued', 'processing', 'ready', 'unsupported', 'failed', 'archived'].map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      <section className={styles.assetGrid}>
        {props.items.map((asset) => (
          <article
            className={styles.assetCard}
            key={asset.id}
            onClick={() => props.onEdit(asset)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                props.onEdit(asset)
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className={styles.assetPreview}>
              {asset.thumbnail_url || asset.derivatives.some((item) => item.variant === 'thumb') ? (
                <img alt="" src={resolveMediaUrl(getVisualDerivativeUrl(asset, 'thumb'))} />
              ) : asset.asset_type === 'image' ? (
                <ImageIcon size={34} />
              ) : (
                <Film size={34} />
              )}
              <span className={[styles.typeBadge, asset.asset_type === 'image' ? styles.typeImage : styles.typeVideo].join(' ')}>
                {asset.asset_type === 'image' ? <ImageIcon size={12} /> : <Film size={12} />}
                {asset.asset_type}
              </span>
              {asset.asset_type === 'video' && formatDuration(asset.duration_seconds) && (
                <span className={styles.durationBadge}>{formatDuration(asset.duration_seconds)}</span>
              )}
              {asset.status !== 'ready' && <StatusBadge className={styles.assetStatusBadge} label={asset.status} />}
            </div>
            <div className={styles.assetBody}>
              <div>
                <h2>{asset.title}</h2>
                <p>{formatVisualAssetMetadata(asset)}</p>
              </div>
              <div className={styles.chips}>
                {asset.groups.map((group) => <Pill key={group.id}>{group.name}</Pill>)}
                {asset.groups.length === 0 && <span className="muted">No groups</span>}
              </div>
              <div className={styles.assetActions}>
                <IconButton
                  className={styles.assetActionLabel}
                  icon={<Edit3 size={15} />}
                  label={`Edit ${asset.title}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    props.onEdit(asset)
                  }}
                  title="Edit"
                >
                  <span>Edit</span>
                </IconButton>
                <IconButton
                  icon={<Trash2 size={15} />}
                  label={`Delete ${asset.title}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    void props.onDelete(asset)
                  }}
                  title="Delete"
                  variant="danger"
                />
              </div>
            </div>
          </article>
        ))}
        {props.items.length === 0 && <EmptyState className="full-width" message="No visual assets match these filters." />}
      </section>

      {showUploadDialog && (
        <Modal
          ariaLabelledBy="upload-visual-title"
          bodyClassName={uploadStyles.uploadSheetBody ?? ''}
          closeLabel="Close"
          mobilePresentation="sheet"
          onClose={closeUploadDialog}
          panelClassName={uploadClass('upload-modal-panel', 'visual-upload-panel')}
          title={<h2 id="upload-visual-title">Upload Visuals</h2>}
        >
          <button
            className={uploadClass('upload-dropzone', draggingUpload && 'dragover')}
            onClick={() => fileInputRef.current?.click()}
            onDragLeave={() => setDraggingUpload(false)}
            onDragOver={(event) => {
              event.preventDefault()
              setDraggingUpload(true)
            }}
            onDrop={(event) => {
              event.preventDefault()
              setDraggingUpload(false)
              void uploadFiles(event.dataTransfer.files)
            }}
            type="button"
          >
            <Upload size={34} />
            <span className={uploadStyles['upload-drop-title']}>Drag and drop files here</span>
            <span className={uploadStyles['upload-drop-subtitle']}>or click to browse - supports MP4, MOV, JPG, PNG, WebP</span>
          </button>
          <input
            accept="video/*,image/*"
            hidden
            multiple
            onChange={(event) => void uploadFiles(event.target.files)}
            ref={fileInputRef}
            type="file"
          />
          <div className="form-group">
            <span className="form-label">Assign to Visual Groups (optional)</span>
            <ChipSelect
              chipClassName={uploadStyles.uploadChip}
              empty={<span className="muted">Create a group first to enable upload assignment.</span>}
              onToggle={toggleUploadGroup}
              options={props.groups.map((group) => ({ id: group.id, label: group.name }))}
              selectedIds={uploadGroupIds}
            />
          </div>
          <div className={uploadStyles['upload-list']}>
            {uploadItems.map((item) => (
              <div className={uploadStyles['upload-item']} key={item.id}>
                <span className={uploadStyles['upload-item-icon']}>
                  {item.type === 'image' ? <ImageIcon size={17} /> : <Film size={17} />}
                </span>
                <span className={uploadStyles['upload-item-info']}>
                  <span className={uploadStyles['upload-item-name']}>{item.name}</span>
                  <span className={uploadStyles['upload-item-size']}>{formatBytes(item.size)}</span>
                  <span className={uploadStyles['upload-progress']}>
                    <span style={{ width: `${item.progress}%` }} />
                  </span>
                </span>
                <span
                  className={uploadClass('upload-status', item.status)}
                  title={item.statusText}
                >
                  {item.status === 'queued' && 'Queued'}
                  {item.status === 'uploading' && `${item.progress}%`}
                  {item.status === 'complete' && 'Complete'}
                  {item.status === 'skipped' && 'Skipped'}
                  {item.status === 'failed' && 'Failed'}
                </span>
              </div>
            ))}
          </div>
          <ModalActions className={uploadClass('uploadDoneActions')}>
            <Button disabled={props.uploading} icon={<Check size={16} />} onClick={closeUploadDialog} variant="primary">
              Done
            </Button>
          </ModalActions>
        </Modal>
      )}
    </>
  )
}
